import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { taskAPI } from '../../utils/crmApi';
import { PlusIcon, PencilIcon, TrashIcon, EyeIcon } from '@heroicons/react/24/outline';
import { useBranding } from '../../contexts/BrandingContext';
import { EmptyStates } from '../../components/ui/empty-state';
import toast from 'react-hot-toast';

const TaskTemplates = () => {
    const navigate = useNavigate();
    const { branding } = useBranding();
    const [loading, setLoading] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');

    const categories = [
        { value: 'all', label: 'All Categories' },
        { value: 'visa', label: 'Visa' },
        { value: 'housing', label: 'Housing' },
        { value: 'banking', label: 'Banking' },
        { value: 'healthcare', label: 'Healthcare' },
        { value: 'education', label: 'Education' },
        { value: 'employment', label: 'Employment' },
        { value: 'legal', label: 'Legal' },
        { value: 'other', label: 'Other' }
    ];

    useEffect(() => {
        loadTemplates();
    }, []);

    const loadTemplates = async () => {
        try {
            setLoading(true);
            const response = await taskAPI.getTemplates();
            if (response.data.success) {
                setTemplates(response.data.data);
            }
        } catch (error) {
            console.error('Failed to load templates:', error);
            toast.error('Failed to load templates');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (templateId) => {
        if (!window.confirm('Are you sure you want to delete this template?')) {
            return;
        }

        try {
            await taskAPI.deleteTemplate(templateId);
            toast.success('Template deleted successfully');
            loadTemplates();
        } catch (error) {
            console.error('Failed to delete template:', error);
            toast.error('Failed to delete template');
        }
    };

    const handleCreateFromTemplate = (templateId) => {
        navigate(`/tasks/new?template=${templateId}`);
    };

    const filteredTemplates = templates.filter(template => {
        const matchesSearch = template.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            template.description?.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesCategory = selectedCategory === 'all' || template.category === selectedCategory;
        return matchesSearch && matchesCategory;
    });

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-foreground">Task Templates</h1>
                    <p className="text-sm text-muted-foreground">
                        Create and manage reusable task templates for your agency
                    </p>
                </div>
                <button
                    onClick={() => navigate('/task-templates/new')}
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                    <PlusIcon className="h-5 w-5 mr-2" />
                    Create Template
                </button>
            </div>

            {/* Filters */}
            <div className="bg-card rounded-lg shadow-sm border border-border p-4 mb-6">
                <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1">
                        <input
                            type="text"
                            placeholder="Search templates..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full px-3 py-2 border border-input rounded-lg b bg-background text-foregroundg-background text-foreground focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                        />
                    </div>
                    <div className="sm:w-48">
                        <select
                            value={selectedCategory}
                            onChange={(e) => setSelectedCategory(e.target.value)}
                            className="w-full px-3 py-2 border border-input rounded-lg b bg-background text-foregroundg-background text-foreground focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                        >
                            {categories.map(category => (
                                <option key={category.value} value={category.value}>
                                    {category.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {/* Templates Grid */}
            {filteredTemplates.length === 0 ? (
                <div className="bg-card rounded-lg shadow-sm border border-border p-12">
                    {searchQuery || selectedCategory !== 'all' ? (
                        <div className="text-center py-12">
                            <h3 className="text-lg font-medium text-foreground mb-2">No templates found</h3>
                            <p className="text-muted-foreground">Try adjusting your search or filter criteria</p>
                        </div>
                    ) : (
                        <EmptyStates.NoTaskTemplates 
                            onCreateTemplate={() => navigate('/task-templates/new')}
                            branding={branding}
                        />
                    )}
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredTemplates.map((template) => (
                        <div key={template._id} className="bg-card rounded-lg shadow-sm border border-border p-6 hover:shadow-md transition-shadow">
                            <div className="flex items-start justify-between mb-4">
                                <div className="flex-1">
                                    <h3 className="text-lg font-semibold text-foreground mb-1">
                                        {template.title}
                                    </h3>
                                    <p className="text-sm text-muted-foreground capitalize">
                                        {template.category}
                                    </p>
                                </div>
                                <div className="flex items-center space-x-1">
                                    <button
                                        onClick={() => navigate(`/task-templates/${template._id}`)}
                                        className="p-1 text-muted-foreground hover:text-muted-foreground"
                                        title="View template"
                                    >
                                        <EyeIcon className="h-4 w-4" />
                                    </button>
                                    <button
                                        onClick={() => navigate(`/task-templates/${template._id}/edit`)}
                                        className="p-1 text-muted-foreground hover:text-muted-foreground"
                                        title="Edit template"
                                    >
                                        <PencilIcon className="h-4 w-4" />
                                    </button>
                                    <button
                                        onClick={() => handleDelete(template._id)}
                                        className="p-1 text-muted-foreground hover:text-red-600"
                                        title="Delete template"
                                    >
                                        <TrashIcon className="h-4 w-4" />
                                    </button>
                                </div>
                            </div>

                            {template.description && (
                                <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                                    {template.description}
                                </p>
                            )}

                            <div className="flex items-center justify-between text-sm text-muted-foreground mb-4">
                                <span>{template.steps?.length || 0} steps</span>
                                <span>Used {template.usageCount || 0} times</span>
                            </div>

                            <div className="flex space-x-2">
                                <button
                                    onClick={() => handleCreateFromTemplate(template._id)}
                                    className="flex-1 px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                                >
                                    Create Task
                                </button>
                                <button
                                    onClick={() => navigate(`/task-templates/${template._id}`)}
                                    className="px-3 py-2 border border-input focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground text-foreground text-sm rounded-lg hover:bg-muted transition-colors"
                                >
                                    View
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default TaskTemplates;
